import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import pb from '../../../lib/pocketbase';
import html2pdf from 'html2pdf.js';
import './QuotesList.css'; // On va créer ce fichier aussi

const QuotesList = () => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [companySettings, setCompanySettings] = useState(null);
    const previewRef = useRef();
    
    // Creation Wizard State
    const [isCreating, setIsCreating] = useState(false);
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        client: null,
        manualClient: { firstName: '', lastName: '', email: '', phone: '' },
        vehicle: { brand: '', model: '', year: '', fuel: '', power: '', plate: '' },
        selectedServices: [],
        customLines: [],
        notes: '',
        total: 0
    });

    // Data for Selection
    const [clients, setClients] = useState([]);
    const [catalog, setCatalog] = useState([]);
    
    useEffect(() => {
        fetchData();
        fetchAuxData();
    }, []);
    
    const fetchData = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('quotes').getFullList({
                sort: '-created',
                expand: 'user'
            });
            setQuotes(records);
            
            // Fetch Company Settings
            const settings = await pb.collection('company_settings').getList(1, 1);
            if (settings.items.length > 0) {
                setCompanySettings(settings.items[0]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuxData = async () => {
        try {
            const [usersRes, servicesRes] = await Promise.all([
                pb.collection('users').getFullList({ sort: 'lastName' }),
                pb.collection('services').getFullList({ filter: 'active = true' })
            ]);
            setClients(usersRes);
            setCatalog(servicesRes);
        } catch (err) {
            console.error(err);
        }
    };

    // Calculation logic
    useEffect(() => {
        const servicesTotal = formData.selectedServices.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
        const customTotal = formData.customLines.reduce((sum, l) => sum + (parseFloat(l.price) || 0), 0);
        setFormData(prev => ({ ...prev, total: servicesTotal + customTotal }));
    }, [formData.selectedServices, formData.customLines]);

    // Handlers
    const handleAddService = (service) => {
        if (formData.selectedServices.find(s => s.id === service.id)) return;
        setFormData(prev => ({
            ...prev,
            selectedServices: [...prev.selectedServices, service]
        }));
    };

    const handleRemoveService = (id) => {
        setFormData(prev => ({
            ...prev,
            selectedServices: prev.selectedServices.filter(s => s.id !== id)
        }));
    };

    const handleDownloadPDF = () => {
        const element = previewRef.current;
        const clientName = formData.client ? `${formData.client.lastName}_${formData.client.firstName}` : (formData.manualClient.lastName ? `${formData.manualClient.lastName}_${formData.manualClient.firstName}` : 'CLIENT_INCONNU');
        const opt = {
            margin:       0,
            filename:     `Devis_LUNIVERS_DIAG_${clientName}_${new Date().toISOString().slice(0,10)}.pdf`,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2, useCORS: true },
            jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().from(element).set(opt).save();
    };

    const handleSaveQuote = async () => {
        try {
            // Calculate final reference
            const prefix = companySettings?.num_devis_prefix || 'DEV';
            const counter = companySettings?.num_devis_counter || 0;
            const fullRef = `${prefix}-${counter.toString().padStart(3, '0')}`;

            const finalData = {
                user: formData.client?.id || null,
                firstName: formData.client?.firstName || formData.manualClient.firstName,
                lastName: formData.client?.lastName || formData.manualClient.lastName,
                email: formData.client?.email || formData.manualClient.email,
                phone: formData.client?.phone || formData.manualClient.phone,
                vehicle_brand: formData.vehicle.brand,
                vehicle_model: formData.vehicle.model,
                vehicle_plate: formData.vehicle.plate,
                vehicle_fuel: formData.vehicle.fuel,
                vehicle_power: formData.vehicle.power,
                total_price: formData.total,
                status: 'pending',
                notes: formData.notes,
                source: 'admin',
                services: JSON.stringify([...formData.selectedServices, ...formData.customLines]),
                // Save the reference for future use/PDF
                reference: fullRef
            };

            // 1. Create the quote
            await pb.collection('quotes').create(finalData);

            // 2. Increment the counter in company_settings
            if (companySettings) {
                await pb.collection('company_settings').update(companySettings.id, {
                    'num_devis_counter+': 1
                });
            }

            setIsCreating(false);
            fetchData();
            // Reset Wizard
            setStep(1);
            setFormData({
                client: null,
                manualClient: { firstName: '', lastName: '', email: '', phone: '' },
                vehicle: { brand: '', model: '', year: '', fuel: '', power: '', plate: '' },
                selectedServices: [],
                customLines: [],
                notes: '',
                total: 0
            });
        } catch (err) {
            alert("Erreur lors de la création : " + err.message);
        }
    };

    if (loading) return <div className="admin-content-wrapper">Chargement...</div>;

    return (
        <div className="admin-content-wrapper">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Gestion des Devis</h2>
                    <p>Suivez vos propositions commerciales et créez-en de nouvelles.</p>
                </div>
                <div className="admin-header-actions">
                    <button className="primary-button" onClick={() => setIsCreating(true)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"></path></svg>
                        Nouveau Devis
                    </button>
                </div>
            </section>

            <section className="card full-width-card">
                <div className="table-responsive">
                    <table className="data-table admin-table">
                        <thead>
                            <tr>
                                <th>Référence</th>
                                <th>Client</th>
                                <th>Véhicule</th>
                                <th>Montant</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.map(q => (
                                <tr key={q.id}>
                                    <td><span className="id-badge">#{q.id.slice(-6).toUpperCase()}</span></td>
                                    <td>
                                        <div className="user-name">{q.firstName} {q.lastName}</div>
                                        <div className="user-sub">{q.email}</div>
                                    </td>
                                    <td>{q.vehicle_brand} {q.vehicle_model} {q.vehicle_plate && `(${q.vehicle_plate})`}</td>
                                    <td><strong>{q.total_price} €</strong></td>
                                    <td><span className={`status-pill ${q.status}`}>{q.status}</span></td>
                                    <td>
                                        <button className="icon-btn" style={{ color: '#3b82f6' }} title="Voir PDF">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6"></path></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* CREATION WIZARD MODAL - SPLIT SCREEN */}
            {isCreating && createPortal(
                <div className="modal-backdrop" onClick={() => setIsCreating(false)} style={{ zIndex: 99999 }}>
                    <div className="modal-content quote-split-wizard" onClick={e => e.stopPropagation()}>
                        
                        {/* LEFT: UNIFIED INPUT PANEL */}
                        <div className="wizard-input-panel">
                            <div className="wizard-header">
                                <div className="admin-header-main">
                                    <h2>Nouveau Devis</h2>
                                    <span className="subtitle-admin">Saisie Rapide</span>
                                </div>
                                <button className="modal-close-mini" onClick={() => setIsCreating(false)}>×</button>
                            </div>

                            <div className="wizard-body unified-scroll-body">
                                {/* SECTION 1: CLIENT */}
                                <section className="wizard-form-section">
                                    <h3 className="section-title-neo">1. Informations Client</h3>
                                    <div className="selection-stack">
                                        <div className="input-group">
                                            <select 
                                                className="admin-input" 
                                                onChange={(e) => {
                                                    const c = clients.find(cl => cl.id === e.target.value);
                                                    setFormData({...formData, client: c});
                                                }}
                                            >
                                                <option value="">-- Client Existant --</option>
                                                {clients.map(c => <option key={c.id} value={c.id}>{c.lastName} {c.firstName}</option>)}
                                            </select>
                                        </div>
                                        <div className="divider-text-or"><span>OU NOUVEAU CLIENT</span></div>
                                        <div className="manual-form-stacked">
                                            <div className="pricing-input-grid">
                                                <input placeholder="Prénom" className="admin-input" value={formData.manualClient.firstName} onChange={e => setFormData({...formData, manualClient: {...formData.manualClient, firstName: e.target.value}})} />
                                                <input placeholder="Nom" className="admin-input" value={formData.manualClient.lastName} onChange={e => setFormData({...formData, manualClient: {...formData.manualClient, lastName: e.target.value}})} />
                                                <input placeholder="Email" className="admin-input" style={{ gridColumn: 'span 2'}} value={formData.manualClient.email} onChange={e => setFormData({...formData, manualClient: {...formData.manualClient, email: e.target.value}})} />
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* SECTION 2: VEHICULE */}
                                <section className="wizard-form-section">
                                    <h3 className="section-title-neo">2. Détails Véhicule</h3>
                                    <div className="vehicle-input-grid">
                                        <div className="form-group-triple">
                                            <input placeholder="Marque" className="admin-input" value={formData.vehicle.brand} onChange={e => setFormData({...formData, vehicle: {...formData.vehicle, brand: e.target.value}})} />
                                            <input placeholder="Modèle" className="admin-input" value={formData.vehicle.model} onChange={e => setFormData({...formData, vehicle: {...formData.vehicle, model: e.target.value}})} />
                                            <input placeholder="Immat." className="admin-input" value={formData.vehicle.plate} onChange={e => setFormData({...formData, vehicle: {...formData.vehicle, plate: e.target.value.toUpperCase()}})} />
                                        </div>
                                        <input placeholder="Motorisation (ex: 2.0 TDI)" className="admin-input" value={formData.vehicle.power} onChange={e => setFormData({...formData, vehicle: {...formData.vehicle, power: e.target.value}})} />
                                    </div>
                                </section>

                                {/* SECTION 3: SERVICES */}
                                <section className="wizard-form-section">
                                    <h3 className="section-title-neo">3. Prestations & Catalogue</h3>
                                    <div className="catalog-search-wizard">
                                        <div className="selection-list-scroll-small">
                                            {catalog.map(s => (
                                                <div key={s.id} className="catalog-item-row-compact" onClick={() => handleAddService(s)}>
                                                    <div className="item-info">
                                                        <strong>{s.name}</strong>
                                                        <span>{s.price} €</span>
                                                    </div>
                                                    <button className="add-btn-mini">+</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="selected-services-mini">
                                        {formData.selectedServices.map(s => (
                                            <div key={s.id} className="selected-tag-row">
                                                <span>{s.name}</span>
                                                <div className="tag-actions">
                                                    <strong>{s.price}€</strong>
                                                    <button className="remove-btn-mini" onClick={() => handleRemoveService(s.id)}>×</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            <div className="wizard-footer-panel">
                                <button className="secondary-button" onClick={() => setIsCreating(false)}>Annuler</button>
                                <button className="complete-btn-neo-large" onClick={handleSaveQuote}>Générer & Enregistrer</button>
                            </div>
                        </div>

                        {/* RIGHT: LIVE DOCUMENT PREVIEW */}
                        <div className="wizard-preview-panel">
                            <button className="download-pdf-btn-icon" onClick={handleDownloadPDF} title="Télécharger le PDF">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"></path></svg>
                            </button>
                            <div className="quote-paper-container">
                                <div className="quote-preview-paper" ref={previewRef}>
                                    <header className="quote-doc-header">
                                        <div className="agency-brand">
                                            <div className="brand-logo-preview">
                                                {companySettings ? (
                                                    <img 
                                                        src={pb.getFileUrl(companySettings, companySettings.logo_light || companySettings.logo)} 
                                                        alt="Logo" 
                                                        style={{ height: '40px', objectFit: 'contain' }} 
                                                    />
                                                ) : '💠'}
                                            </div>
                                            <div className="brand-text">
                                                <span className="name">{companySettings?.company_name || "L'UNIVERS DIAG"}</span>
                                                <span className="sub">{companySettings?.slogan || "Automobile & Performance"}</span>
                                            </div>
                                        </div>
                                        <div className="doc-meta">
                                            <h2>DEVIS</h2>
                                            <span className="ref">#{companySettings?.num_devis_prefix || 'DEV'}-{(companySettings?.num_devis_counter || 0).toString().padStart(3, '0')}</span>
                                            <span className="date">Le {new Date().toLocaleDateString('fr')}</span>
                                        </div>
                                    </header>

                                    <div className="quote-parties-grid">
                                        <div className="party-box from">
                                            <span className="party-label">Prestataire</span>
                                            <strong>{companySettings?.company_name || "L'Univers Diag"}</strong>
                                            <p>{companySettings?.address_office || companySettings?.address || '25 Rue de la Performance'}</p>
                                            <p>{companySettings?.billing_zip || ''} {companySettings?.billing_city || 'Toulouse'}</p>
                                            <p>{companySettings?.phone_public || companySettings?.phone || ''}</p>
                                        </div>
                                        <div className="party-box to">
                                            <span className="party-label">Client</span>
                                            <strong>{formData.client ? `${formData.client.firstName} ${formData.client.lastName}` : (formData.manualClient.lastName ? `${formData.manualClient.firstName} ${formData.manualClient.lastName}` : 'Client non renseigné')}</strong>
                                            <p>{formData.manualClient.email || formData.client?.email || 'N/A'}</p>
                                            <p>{formData.vehicle.brand} {formData.vehicle.model} {formData.vehicle.plate ? `(${formData.vehicle.plate})` : ''}</p>
                                        </div>
                                    </div>

                                    <table className="quote-table-preview">
                                        <thead>
                                            <tr>
                                                <th>Désignation de la prestation</th>
                                                <th className="text-right">Total (TTC)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.selectedServices.map(s => (
                                                <tr key={s.id}>
                                                    <td>{s.name}</td>
                                                    <td className="text-right">{s.price} €</td>
                                                </tr>
                                            ))}
                                            {formData.selectedServices.length === 0 && (
                                                <tr>
                                                    <td colSpan="2" className="text-center empty-doc">Sélectionnez des prestations à gauche...</td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td className="total-label">TOTAL GÉNÉRAL TTC</td>
                                                <td className="total-value">{formData.total} €</td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                        <div className="quote-doc-footer" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
                                            <div className="legal-notes">
                                                {companySettings?.footer_devis ? (
                                                    <p>{companySettings.footer_devis}</p>
                                                ) : (
                                                    <p>Validité de l'offre : 30 jours à compter de ce jour.</p>
                                                )}
                                                {companySettings?.cond_reglement_text && (
                                                    <p>Règlement : {companySettings.cond_reglement_text}</p>
                                                )}
                                                {companySettings?.cond_acompte_pct > 0 && (
                                                    <p>Acompte requis : {companySettings.cond_acompte_pct}% à la commande.</p>
                                                )}
                                                <p>{companySettings?.footer_facture || "TVA non applicable, art. 293 B du CGI."}</p>
                                                
                                                {/* Step 5: Bank Details */}
                                                {companySettings?.bank_iban && (
                                                    <div className="bank-details-box-mini" style={{ marginTop: '10px', fontSize: '0.75rem', opacity: 0.8 }}>
                                                        <strong>Coordonnées bancaires pour acompte :</strong><br/>
                                                        {companySettings.bank_name} - IBAN : {companySettings.bank_iban} - BIC : {companySettings.bank_bic}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Step 4: Manager Signature */}
                                            <div className="signature-area">
                                                <span>Cachet & Signature (Agence)</span>
                                                <div className="signature-stamp-placeholder">
                                                    <strong>{pb.authStore.model?.firstName} {pb.authStore.model?.lastName}</strong><br/>
                                                    <span style={{ fontSize: '0.7rem' }}>Gérant de l'Agence</span>
                                                </div>
                                                <div className="signature-line" />
                                                <span className="date-place">Le ..../..../.... à ...................</span>
                                            </div>
                                        </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default QuotesList;
